'use client';

// TODO: this file needs a general cleanup
import React from 'react';

import { UseFormReturn } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { getIntlText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/Button';
import Checkbox from '@/components/atoms/Checkbox';
import { Input, InputFieldProps } from '@/components/atoms/Input';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import { SvgIcon } from '@/components/SvgIcon';

import { FileUploadFormControlProps } from '@/blocks/JobApplicationForm/components/FileUploadField';
import { INFO_MAX_LENGTH } from '@/blocks/JobApplicationForm/constants/form';
import { BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { slugify } from '@/cms/utils';
import { getRichTextFieldHeadingLevel2 } from '@/features/blog/SideNavigation/utils';

import { FileUploadField } from '../JobApplicationForm/components/FileUploadField';

import iconInfoSrc from '~/icons/info-circle.svg';
import iconSpinnerSrc from '~/svg/Spinner.svg';

const FILE_UPLOAD_ACCEPTED_FORMATS = ['png', 'jpg', 'pdf', 'mp4'];

type ConditionallyShow<T extends Record<string, any>> = T & {
  shouldShow: boolean;
};

export type DataCollectionFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
  companyName?: string;
  attachment?: File[];
  termsAndConditionsChecked?: boolean;
};

type Props = {
  title: RichTextFieldStrapiProps;
  description: RichTextFieldStrapiProps;
  termsAndConditions: RichTextFieldStrapiProps;
  submitButtonText: RichTextFieldStrapiProps;

  shouldUseReducedLayout?: boolean;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  errors?: {
    [K in keyof DataCollectionFormValues]?: string | null;
  } & {
    apiError?: string;
    mandatoryFieldsError?: string;
  };
  form: UseFormReturn<DataCollectionFormValues>;

  fieldProps: {
    firstNameProps: ConditionallyShow<InputFieldProps>;
    lastNameProps: ConditionallyShow<InputFieldProps>;
    emailProps: ConditionallyShow<InputFieldProps>;
    companyNameProps: ConditionallyShow<InputFieldProps>;
    messageProps: ConditionallyShow<InputFieldProps & { as: 'textarea' }>;
    uploadFieldProps: ConditionallyShow<Partial<FileUploadFormControlProps>>;
    tnCProps: {
      variant: 'richText' | 'checkbox';
      shouldShow: boolean;
    };
  };
};

const DataCollectionForm = ({
  form,
  title,
  description,
  fieldProps,
  handleSubmit,
  submitButtonText,
  termsAndConditions,
  errors,
  shouldUseReducedLayout,
}: Props) => {
  const { texts } = useTranslation();
  const titleId = slugify(getRichTextFieldHeadingLevel2(title)[0] ?? '');
  
  const TnCContent = () => {
    return fieldProps.tnCProps.variant === 'richText' ? (
      <RichTextFieldStrapi block={termsAndConditions} />
    ) : (
      <Checkbox
        label={<RichTextFieldStrapi block={termsAndConditions} />}
        {...form.getFieldProps('termsAndConditionsChecked')}
        value='consent'
        checked={form.values.termsAndConditionsChecked}
        onChange={(e) =>
          form.setFieldValue(
            'termsAndConditionsChecked',
            e.target
              .checked as DataCollectionFormValues['termsAndConditionsChecked']
          )
        }
      />
    );
  };

  return (
    <div className='md:p-10 rounded-lg flex justify-center items-center flex-col md:flex-row gap-8 md:mx-auto'>
      <div className='w-full flex-1'>
        <BlockHeaderContent
          id={titleId || undefined}
          title={<RichTextFieldStrapi block={title} />}
          alignment={shouldUseReducedLayout ? 'center' : undefined}
          classNames={{
            title: 'font-serif',
            subtitle: 'text-body-l font-mono text-gray-500',
            children: 'flex-col flex-nowrap!',
          }}
          subtitle={ <RichTextFieldStrapi block={description} />}
        >
          <form
            className={cn(
              'md:w-[720px] mx-auto w-full',
              shouldUseReducedLayout
                ? 'flex flex-col md:flex-row items-center justify-center md:gap-4'
                : 'flex flex-col'
            )}
            onSubmit={handleSubmit}
            data-test-id='lead-collection-form'
          >
            <div className='w-full'>
              <div
                className={cn(
                  shouldUseReducedLayout &&
                    'md:flex w-full gap-4 justify-center'
                )}
              >
                {!shouldUseReducedLayout && (
                  <div className='md:flex md:gap-x-4'>
                    {fieldProps.firstNameProps.shouldShow && (
                      <Input
                        placeholder={texts.lead_popup.form.name}
                        hideBottomText={true}
                        classNames={{
                          root: cn('w-full text-gray-950 font-mono pb-4'),
                          bottomText: { root: 'h-4' },
                        }}
                        {...form.getFieldProps('firstName')}
                        {...fieldProps.firstNameProps}
                      />
                    )}
                    {fieldProps.companyNameProps.shouldShow && (
                      <Input
                        placeholder={texts.lead_popup.form.company_name}
                        hideBottomText={true}
                        {...form.getFieldProps('companyName')}
                        classNames={{
                          root: 'w-full text-gray-950 font-mono pb-4',
                          bottomText: { root: 'h-4' },
                        }}
                        {...fieldProps.companyNameProps}
                      />
                    )}
                    {fieldProps.lastNameProps.shouldShow && (
                      <Input
                        placeholder={texts.lead_popup.form.surname}
                        {...form.getFieldProps('lastName')}
                        classNames={{
                          root: 'w-full text-gray-950 font-mono',
                          bottomText: { root: 'h-4' },
                        }}
                        {...fieldProps.lastNameProps}
                      />
                    )}
                  </div>
                )}
                {fieldProps?.emailProps?.shouldShow && (
                  <Input
                    {...form.getFieldProps('email')}
                    placeholder={texts.lead_popup.form.enter_your_email}
                    hideBottomText={!shouldUseReducedLayout}
                    classNames={{
                      root: cn(
                        'w-full md:min-w-[440px] text-gray-950 font-mono',
                        shouldUseReducedLayout ? 'md:max-w-[440px]' : 'pb-4'
                      ),
                      bottomText: {
                        root: cn(shouldUseReducedLayout && 'h-7'),
                      },
                    }}
                    {...fieldProps.emailProps}
                  />
                )}
                {fieldProps?.messageProps.shouldShow && (
                  <Input
                    placeholder={texts.lead_popup.form.message}
                    {...form.getFieldProps('message')}
                    hideBottomText={true}
                    maxLength={INFO_MAX_LENGTH}
                    classNames={{
                      root: 'w-full mb-4',
                    }}
                    {...fieldProps.messageProps}
                    as='textarea'
                  />
                )}
                {fieldProps?.uploadFieldProps?.shouldShow && (
                  <FileUploadField
                    label={null}
                    inputLabels={{
                      label: getIntlText(
                        texts.lead_popup.form.attach_file_types,
                        {
                          fileTypes:
                            FILE_UPLOAD_ACCEPTED_FORMATS.join('/').toUpperCase(),
                        }
                      ),
                    }}
                    acceptedFileTypes={FILE_UPLOAD_ACCEPTED_FORMATS}
                    {...form.getFieldProps(
                      'attachment' as keyof DataCollectionFormValues
                    )}
                    value={form.values.attachment || []}
                    onChange={(files: File[]) =>
                      form.setFieldValue(
                        'attachment',
                        files as DataCollectionFormValues['attachment']
                      )
                    }
                    onFileRemove={() =>
                      form.setFieldValue(
                        'attachment',
                        [] as DataCollectionFormValues['attachment']
                      )
                    }
                    {...fieldProps.uploadFieldProps}
                  />
                )}
                {!shouldUseReducedLayout && (
                  <>
                    {errors?.mandatoryFieldsError && (
                      <div className='flex items-center gap-2 bg-error-100 text-body-s p-3 font-mono mb-4 rounded'>
                        <SvgIcon
                          src={iconInfoSrc}
                          className='h-4 w-4 text-error-500'
                        />
                        {errors.mandatoryFieldsError}
                      </div>
                    )}
                  </>
                )}
                {!shouldUseReducedLayout && (
                  <div className='mb-4 w-full md:w-auto text-gray-950 text-body-m font-mono'>
                    <TnCContent />
                  </div>
                )}
                <div
                  className={cn(
                    !shouldUseReducedLayout &&
                      'flex flex-col md:flex-row gap-4 items-center w-full mb-4 md:justify-between'
                  )}
                >
                  <Button
                    classNames={{
                      root: 'w-full md:w-auto md:min-w-[164px] mb-4 md:mb-0',
                    }}
                    type='submit'
                  >
                    {form.isLoading ? (
                      <span className='flex items-center gap-2'>
                        <SvgIcon
                          src={iconSpinnerSrc}
                          asImg
                          className=' animate-[spin_2s_linear_infinite]'
                        />
                        {texts.submission_status_message.loading}
                      </span>
                    ) : (
                      <RichTextFieldStrapi block={submitButtonText} />
                    )}
                  </Button>
                </div>
              </div>
              {shouldUseReducedLayout && (
                <div className='md:justify-center font-mono md:text-center max-w-[620px] text-gray-950 text-body-m'>
                  <TnCContent />
                </div>
              )}
              {errors?.apiError && (
                <Text className='text-error-500'>{errors.apiError}</Text>
              )}
            </div>
          </form>
        </BlockHeaderContent>
      </div>
    </div>
  );
};

export default DataCollectionForm;
